import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cx } from '../cx.js';

/**
 * Fields that cannot be built without a label.
 *
 * The label is a required prop rather than something a caller remembers to
 * add, and the id wiring is done here rather than passed in. That is the whole
 * reason these wrappers exist: an unlabelled input is the single most common
 * accessibility failure in a React codebase, and it is invisible to everyone
 * who can see the placeholder.
 *
 * Errors are wired through `aria-describedby` and `aria-invalid` together.
 * Red text alone tells a screen reader nothing, and tells a colourblind user
 * nothing either.
 */

const CONTROL =
  'w-full rounded-control border bg-surface px-3 text-ui text-ink ' +
  'placeholder:text-ink-muted transition-colors duration-fast ease-out ' +
  'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-muted';

const HEIGHT = 'min-h-[44px]';

function borderFor(invalid: boolean): string {
  return invalid ? 'border-danger' : 'border-line-control';
}

/**
 * `| undefined` is spelled out on every optional member because this package
 * compiles under `exactOptionalPropertyTypes`. Without it, forwarding a
 * destructured `hint` — which is `string | undefined` once it has been read —
 * into this shape is a type error, and the tempting fix is to stop forwarding
 * it.
 */
interface Described {
  readonly label: string;
  /** Shown under the control, always. Not a tooltip: nobody hovers on a phone. */
  readonly hint?: string | undefined;
  readonly error?: string | null | undefined;
  /** Hides the label visually but keeps it for assistive technology. */
  readonly labelHidden?: boolean | undefined;
}

function Wrapper({
  label,
  hint,
  error,
  labelHidden = false,
  controlId,
  hintId,
  errorId,
  children,
}: Described & {
  readonly controlId: string;
  readonly hintId: string;
  readonly errorId: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={controlId}
        className={cx('block text-ui-sm font-medium text-ink', labelHidden && 'sr-only')}
      >
        {label}
      </label>
      {children}
      {hint !== undefined && hint !== '' && (
        <p id={hintId} className="text-caption text-ink-muted">
          {hint}
        </p>
      )}
      {error !== undefined && error !== null && error !== '' && (
        <p id={errorId} className="text-caption font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** The `aria-describedby` value, or undefined — never an empty string. */
function describedBy(
  hint: string | undefined,
  error: string | null | undefined,
  hintId: string,
  errorId: string,
): string | undefined {
  const parts = [
    hint !== undefined && hint !== '' ? hintId : null,
    error !== undefined && error !== null && error !== '' ? errorId : null,
  ].filter((part): part is string => part !== null);
  return parts.length === 0 ? undefined : parts.join(' ');
}

export type InputProps = Described &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'>;

export function Input({ label, hint, error, labelHidden, ...rest }: InputProps): React.JSX.Element {
  const base = useId();
  const controlId = `${base}-control`;
  const hintId = `${base}-hint`;
  const errorId = `${base}-error`;
  const invalid = error !== undefined && error !== null && error !== '';

  return (
    <Wrapper
      label={label}
      hint={hint}
      error={error}
      labelHidden={labelHidden}
      controlId={controlId}
      hintId={hintId}
      errorId={errorId}
    >
      <input
        id={controlId}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(hint, error, hintId, errorId)}
        className={cx(CONTROL, HEIGHT, borderFor(invalid))}
        {...rest}
      />
    </Wrapper>
  );
}

export type TextareaProps = Described &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'id'>;

export function Textarea({
  label,
  hint,
  error,
  labelHidden,
  rows = 4,
  ...rest
}: TextareaProps): React.JSX.Element {
  const base = useId();
  const controlId = `${base}-control`;
  const hintId = `${base}-hint`;
  const errorId = `${base}-error`;
  const invalid = error !== undefined && error !== null && error !== '';

  return (
    <Wrapper
      label={label}
      hint={hint}
      error={error}
      labelHidden={labelHidden}
      controlId={controlId}
      hintId={hintId}
      errorId={errorId}
    >
      <textarea
        id={controlId}
        rows={rows}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(hint, error, hintId, errorId)}
        className={cx(CONTROL, 'py-2.5', borderFor(invalid))}
        {...rest}
      />
    </Wrapper>
  );
}

export type SelectProps = Described &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'id'> & {
    readonly children: ReactNode;
  };

/**
 * A native `<select>`, on purpose.
 *
 * A custom listbox is four hundred lines of keyboard handling that a platform
 * already ships, and on a phone the native control is a full-screen picker
 * that beats anything drawn in a div.
 */
export function Select({
  label,
  hint,
  error,
  labelHidden,
  children,
  ...rest
}: SelectProps): React.JSX.Element {
  const base = useId();
  const controlId = `${base}-control`;
  const hintId = `${base}-hint`;
  const errorId = `${base}-error`;
  const invalid = error !== undefined && error !== null && error !== '';

  return (
    <Wrapper
      label={label}
      hint={hint}
      error={error}
      labelHidden={labelHidden}
      controlId={controlId}
      hintId={hintId}
      errorId={errorId}
    >
      <select
        id={controlId}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(hint, error, hintId, errorId)}
        className={cx(CONTROL, HEIGHT, borderFor(invalid))}
        {...rest}
      >
        {children}
      </select>
    </Wrapper>
  );
}

export type CheckboxProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'id' | 'type'
> & {
  readonly label: ReactNode;
  readonly hint?: string;
};

export function Checkbox({ label, hint, ...rest }: CheckboxProps): React.JSX.Element {
  const base = useId();
  const controlId = `${base}-control`;
  const hintId = `${base}-hint`;
  return (
    <div className="flex gap-3">
      <input
        id={controlId}
        type="checkbox"
        aria-describedby={hint === undefined ? undefined : hintId}
        className="mt-0.5 h-5 w-5 shrink-0 rounded-[4px] border border-line-control text-teal-ink accent-teal-ink"
        {...rest}
      />
      <div className="min-w-0">
        <label htmlFor={controlId} className="block text-ui text-ink">
          {label}
        </label>
        {hint !== undefined && (
          <p id={hintId} className="mt-0.5 text-caption text-ink-muted">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}

export type RadioProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'id' | 'type'
> & {
  readonly label: ReactNode;
  readonly hint?: string;
};

export function Radio({ label, hint, ...rest }: RadioProps): React.JSX.Element {
  const base = useId();
  const controlId = `${base}-control`;
  const hintId = `${base}-hint`;
  return (
    <div className="flex gap-3">
      <input
        id={controlId}
        type="radio"
        aria-describedby={hint === undefined ? undefined : hintId}
        className="mt-0.5 h-5 w-5 shrink-0 border border-line-control accent-teal-ink"
        {...rest}
      />
      <div className="min-w-0">
        <label htmlFor={controlId} className="block text-ui text-ink">
          {label}
        </label>
        {hint !== undefined && (
          <p id={hintId} className="mt-0.5 text-caption text-ink-muted">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * A switch.
 *
 * A real `<input type="checkbox">` underneath with `role="switch"`, not a div
 * with an onClick: the keyboard, the form, and every assistive technology
 * already know what a checkbox is. The visual track is drawn with a peer
 * selector rather than by re-implementing the state.
 */
export function Switch({
  label,
  hint,
  checked,
  onChange,
  disabled = false,
  name,
  labelHidden = false,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  readonly disabled?: boolean;
  readonly name?: string;
  /**
   * Hides the label visually, never from assistive technology.
   *
   * For a switch inside a row that already names what it governs, where a
   * second copy of the name is noise on screen and the only thing standing
   * between a screen-reader user and an unlabelled checkbox.
   */
  readonly labelHidden?: boolean;
}): React.JSX.Element {
  const base = useId();
  const controlId = `${base}-control`;
  const hintId = `${base}-hint`;
  return (
    <div className={cx('flex items-start gap-4', labelHidden ? '' : 'justify-between')}>
      <div className={cx('min-w-0', labelHidden && 'sr-only')}>
        <label htmlFor={controlId} className="block text-ui font-medium text-ink">
          {label}
        </label>
        {hint !== undefined && (
          <p id={hintId} className="mt-0.5 text-caption text-ink-muted">
            {hint}
          </p>
        )}
      </div>
      <span className="relative inline-flex shrink-0">
        <input
          id={controlId}
          name={name}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          aria-describedby={hint === undefined ? undefined : hintId}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
          className="peer h-6 w-11 cursor-pointer appearance-none rounded-pill border border-line-control bg-surface-muted transition-colors duration-fast ease-out checked:border-teal-ink checked:bg-teal-ink disabled:cursor-not-allowed disabled:opacity-50"
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-pill bg-surface transition-transform duration-fast ease-out peer-checked:translate-x-5"
        />
      </span>
    </div>
  );
}
